import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import type { EChartsCoreOption } from 'echarts';

import { OpsSummaryService } from '../../../core/ops/ops-summary.service';
import {
  NEXT_ACTOR_META,
  NextActor,
  OpsOverdueItem,
  OpsPipelineStage,
  OpsPreApplicationItem,
  OpsQueue,
  OpsQueueSummary,
  OpsRecentApplication,
  OpsWorklistItem,
} from '../../../core/ops/ops-summary.types';
import { PRE_RECOMMENDATION_LABELS } from '../../../core/applications/pre-application.types';
import { KS_STAGE_META } from '../../../core/kickstart/kickstart.types';
import { DF_STAGE_META } from '../../../core/df/df.types';
import { aiRecommendationLabel } from '../../../core/status/status-meta.types';
import { ThemeService } from '../../../core/theme/theme.service';
import { ChartComponent } from '../../../shared/chart/chart';
import { aiMixChart, intakeChart, stageMixChart } from './ops-dashboard-charts';
import { IconComponent, IconName } from '../../../shared/icon/icon';
import { DatePipe, LowerCasePipe } from '@angular/common';
import { ZarPipe } from '../../../shared/pipes/zar.pipe';
import { DashPipe } from '../../../shared/pipes/dash.pipe';

type QueueKey = 'pre_applications' | 'ks' | 'df_facilities' | 'df_invoices';
type QueueGroupKey = 'PRE' | 'KS' | 'DF';

interface QueueTile {
  readonly key: QueueKey;
  readonly icon: IconName;
  readonly modifier: string;
  /** Function-level tile label (the product is the group heading). */
  readonly label: string;
  readonly sub: string;
  readonly queue: OpsQueue;
  /** False for pre-applications, which carry no SLA (ruling Q4). */
  readonly hasSla: boolean;
}

/** Queues grouped under their product, so DF Facilities + DF Invoices read as
 *  Debtors Finance function-queues rather than peers of Kickstart Cash. */
interface QueueGroup {
  readonly product: QueueGroupKey;
  /** The full product name, written out (canon: never abbreviated). */
  readonly productLabel: string;
  readonly tiles: QueueTile[];
}

const QUEUE_META: Record<string, { icon: IconName; modifier: string; label: string; sub: string }> =
  {
    pre_applications: {
      icon: 'inbox',
      modifier: 'pre',
      label: 'Submissions',
      sub: 'waiting for a decision',
    },
    ks: { icon: 'zap', modifier: 'ks', label: 'Applications', sub: 'waiting for review' },
    df_facilities: {
      icon: 'briefcase',
      modifier: 'df-fac',
      label: 'Facilities',
      sub: 'waiting for review',
    },
    df_invoices: {
      icon: 'wallet',
      modifier: 'df-inv',
      label: 'Invoices',
      sub: 'awaiting verification',
    },
  };

/** Which queues belong to which product, in display order. */
const QUEUE_GROUPS: readonly {
  product: QueueGroupKey;
  productLabel: string;
  keys: readonly QueueKey[];
}[] = [
  { product: 'PRE', productLabel: 'Pre-applications', keys: ['pre_applications'] },
  { product: 'KS', productLabel: 'Kickstart Cash', keys: ['ks'] },
  { product: 'DF', productLabel: 'Debtors Finance', keys: ['df_facilities', 'df_invoices'] },
];

type WorklistLens = 'mine' | 'unassigned' | 'all';

@Component({
  selector: 'app-ops-dashboard',
  imports: [RouterLink, ChartComponent, IconComponent, DatePipe, LowerCasePipe, ZarPipe, DashPipe],
  templateUrl: './ops-dashboard.html',
  styleUrl: './ops-dashboard.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OpsDashboardComponent implements OnInit {
  private readonly summaryService = inject(OpsSummaryService);
  private readonly themeService = inject(ThemeService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);

  @ViewChild('worklistSection') worklistSection?: ElementRef<HTMLElement>;

  protected readonly summary = signal<OpsQueueSummary | null>(null);
  protected readonly isLoading = signal(true);
  protected readonly loadError = signal(false);

  /** Which worklist lens is active: mine / unassigned / all. */
  protected readonly lens = signal<WorklistLens>('all');

  /** ID of the row currently having its pick-up in flight (prevents double-submit). */
  protected readonly pickingUp = signal<string | null>(null);

  /** Queue tiles grouped under their product (Kickstart Cash / Debtors Finance),
   *  so Facilities + Invoices read as Debtors Finance queues, not peer products. */
  protected readonly queueGroups = computed<QueueGroup[]>(() => {
    const s = this.summary();
    if (!s) return [];
    return QUEUE_GROUPS.map((group) => ({
      product: group.product,
      productLabel: group.productLabel,
      tiles: group.keys.map((key) => ({
        key,
        icon: QUEUE_META[key].icon,
        modifier: QUEUE_META[key].modifier,
        label: QUEUE_META[key].label,
        sub: QUEUE_META[key].sub,
        queue: s.queues[key],
        hasSla: key !== 'pre_applications',
      })),
    }));
  });

  protected readonly preApplicationsWaiting = computed<OpsPreApplicationItem[]>(
    () => this.summary()?.pre_applications_waiting ?? [],
  );
  protected readonly preApplicationsWaitingCount = computed(
    () => this.summary()?.queues.pre_applications.waiting ?? 0,
  );

  protected readonly totalWaiting = computed(() => this.summary()?.metrics.total_waiting ?? 0);
  protected readonly totalBreaching = computed(() => this.summary()?.metrics.total_breaching ?? 0);

  protected readonly overdue = computed<OpsOverdueItem[]>(() => this.summary()?.overdue ?? []);

  /** Newest applications across both products (consolidated from admin). */
  protected readonly recentApplications = computed<OpsRecentApplication[]>(
    () => this.summary()?.recent_applications ?? [],
  );

  // ─── Pipeline strip ────────────────────────────────────────────────────────
  protected readonly pipelineStages = computed<OpsPipelineStage[]>(
    () => this.summary()?.pipeline?.stages ?? [],
  );

  protected readonly pipelineTotal = computed(() => this.summary()?.pipeline?.total ?? 0);

  protected readonly slowestStage = computed(() => this.summary()?.slowest_stage ?? null);

  // ─── Worklist + lens ───────────────────────────────────────────────────────
  protected readonly mineCount = computed(() => this.summary()?.mine_count ?? 0);
  protected readonly unassignedCount = computed(() => this.summary()?.unassigned_count ?? 0);

  /** Raw worklist from backend (pre-sorted). */
  private readonly worklist = computed<OpsWorklistItem[]>(() => this.summary()?.worklist ?? []);

  /** Worklist filtered by the active lens. */
  protected readonly filteredWorklist = computed<OpsWorklistItem[]>(() => {
    const items = this.worklist();
    switch (this.lens()) {
      case 'mine':
        return items.filter((i) => i.assignee_is_me);
      case 'unassigned':
        return items.filter((i) => !i.assignee);
      default:
        return items;
    }
  });

  /** Contextual hero sub-line. */
  protected readonly heroSub = computed(() => {
    const s = this.summary();
    if (!s) return 'Review and action pre-applications and applications for both products.';
    if (s.metrics.total_breaching > 0) {
      const n = s.metrics.total_breaching;
      return `${n} item${n === 1 ? '' : 's'} ${n === 1 ? 'is' : 'are'} breaching SLA — act first.`;
    }
    const pre = s.queues.pre_applications.waiting;
    const apps = s.metrics.total_waiting;
    if (pre + apps === 0) {
      return 'All clear — nothing waiting in the queues.';
    }
    const parts = [];
    if (pre > 0) parts.push(`${pre} pre-application${pre === 1 ? '' : 's'}`);
    if (apps > 0) parts.push(`${apps} application${apps === 1 ? '' : 's'}`);
    return `${parts.join(' and ')} waiting for review.`;
  });

  // ─── Charts — pure builders in ops-dashboard-charts.ts ────────────────────
  protected readonly aiChartOption = computed<EChartsCoreOption>(() =>
    aiMixChart(this.summary()?.recommendations ?? {}, this.themeService.isDark()),
  );
  protected readonly statusChartOption = computed<EChartsCoreOption>(() =>
    stageMixChart(this.summary()?.status_breakdown, this.themeService.isDark()),
  );
  protected readonly intakeChartOption = computed<EChartsCoreOption>(() =>
    intakeChart(this.summary()?.intake, this.themeService.isDark()),
  );

  ngOnInit(): void {
    this.summaryService
      .get()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data) => {
          this.summary.set(data);
          this.isLoading.set(false);
          this.cdr.markForCheck();
        },
        error: () => {
          this.isLoading.set(false);
          this.loadError.set(true);
          this.cdr.markForCheck();
        },
      });
  }

  /** Express overdue business-hours as whole business days (9h/day, SAST workday). */
  protected overdueLabel(hours: number): string {
    const days = hours / 9;
    if (days >= 1) {
      const d = Math.round(days * 10) / 10;
      return `${d} business day${d === 1 ? '' : 's'} overdue`;
    }
    return `${Math.round(hours)}h overdue`;
  }

  /** Express hours-in-stage as business days (same 9h convention as overdueLabel). */
  protected hoursInStageLabel(hours: number): string {
    const days = hours / 9;
    if (days >= 1) {
      const d = Math.round(days * 10) / 10;
      return `${d} business day${d === 1 ? '' : 's'}`;
    }
    return `${Math.round(hours)}h`;
  }

  protected preRecommendationLabel(item: OpsPreApplicationItem): string {
    return PRE_RECOMMENDATION_LABELS[item.recommendation] ?? item.recommendation;
  }

  protected readonly aiRecLabel = aiRecommendationLabel;

  protected aiRecClass(rec: string): string {
    const map: Record<string, string> = {
      APPROVE: 'ai-rec--approve',
      MORE_INFO: 'ai-rec--more-info',
      DECLINE: 'ai-rec--decline',
    };
    return map[rec] ?? '';
  }

  /** Human label for an application stage (KS and DF share the stage keys). */
  protected stageLabel(stage: string): string {
    return (
      KS_STAGE_META[stage as keyof typeof KS_STAGE_META]?.label ??
      DF_STAGE_META[stage as keyof typeof DF_STAGE_META]?.label ??
      stage
    );
  }

  protected actorClass(actor: NextActor): string {
    return NEXT_ACTOR_META[actor].cssClass;
  }

  protected nextActorLabel(actor: NextActor): string {
    return NEXT_ACTOR_META[actor].label;
  }

  /**
   * Initials (up to 2 chars) from a display name — used for assignee avatar.
   * Returns '' when name is empty.
   */
  protected initials(name: string): string {
    if (!name) return '';
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }

  /**
   * Whether the current user's tier means this CC-required item is above their
   * limit. CREDIT_COMMITTEE members can action it; analysts and senior ops cannot.
   */
  protected isAboveLimit(requiresCc: boolean): boolean {
    const tier = this.summary()?.current_user_tier;
    return requiresCc && tier !== 'CREDIT_COMMITTEE';
  }

  /** Set the worklist lens and scroll the worklist into view. */
  protected setLens(lens: WorklistLens, _stageKey?: string): void {
    this.lens.set(lens);
    // If a pipeline segment was clicked we could filter by stage too, but the
    // lens model (mine/unassigned/all) is sufficient for the worklist filter.
    // The scroll brings the worklist into view after the filter change.
    setTimeout(() => {
      this.worklistSection?.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  }

  /**
   * Assign the current user to a worklist item.
   * Optimistically updates the row; falls back to a full refetch on 409 conflict.
   */
  protected pickUp(item: OpsWorklistItem): void {
    const key = item.short_id;
    if (this.pickingUp() === key) return;
    this.pickingUp.set(key);

    this.summaryService
      .pickUp(item.product, item.short_id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          // Optimistically patch the worklist row in the signal.
          const s = this.summary();
          if (s) {
            const patched = s.worklist.map((w) =>
              w.short_id === key
                ? {
                    ...w,
                    assignee: res.assignee,
                    assignee_id: res.assignee_id,
                    assignee_is_me: true,
                    can_pick_up: false,
                    review_stage: 'IN_REVIEW' as const,
                    review_stage_label: 'In review',
                    next_step_label: 'In assessment',
                    next_actor: 'OPS' as const,
                  }
                : w,
            );
            this.summary.set({ ...s, worklist: patched, mine_count: s.mine_count + 1 });
          }
          this.pickingUp.set(null);
          this.cdr.markForCheck();
        },
        error: (err) => {
          this.pickingUp.set(null);
          if (err?.status === 409) {
            // Someone else grabbed it first — refetch the full summary.
            this.summaryService
              .get()
              .pipe(takeUntilDestroyed(this.destroyRef))
              .subscribe({
                next: (data) => {
                  this.summary.set(data);
                  this.cdr.markForCheck();
                },
              });
          }
          // 404 is silent — the row will stay but the next refetch will remove it.
          this.cdr.markForCheck();
        },
      });
  }
}
